import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { VocabularyDisplay } from "@/components/content/vocabulary-display";
import { Button } from "@/components/ui/button";
import { BookOpen, Sparkles, Brain, Eye, EyeOff, BookOpen as BookOpenIcon } from 'lucide-react';
import { VocabularyFloatingActionButtons } from './VocabularyFloatingActionButtons';
import { EmptyState } from '../ui/interactive-empty-state';
import { SpinnerIcon } from '../ui/SpinnerIcon';
import { useI18n } from "@/lib/i18n";
import { useUsage } from "@/contexts/usage-context";
import { useUser } from "@/lib/context/user-context";
import { cn } from "@/lib/utils";
import { createVocabularyTermKey } from "@/lib/vocabulary-term-key";
import { UpgradePromptDialog } from '../ui/upgrade-prompt-dialog';

interface VocabularySectionProps {
  vocabulary: any | null;
  isVocabularyLoading: boolean;
  onGenerateVocabulary: () => void | Promise<void>;
  onTimestampClick?: (timestamp: number, endTimestamp?: number) => void;
  episodeId?: string;
  onVocabularyUpdate?: (enhancedVocabulary: any) => void;
  onCardSwipe?: (
    payload: { term: string; scenarioName?: string },
    direction: "left" | "right"
  ) => void;
  hiddenTermKeys?: string[];
  onFilterHiddenTermsChange?: (keys: string[]) => void;
  session?: any;
  userSettings?: any;
  episodeTitle?: string;
  episodeThumbnail?: string;
  transcript?: any;
  episodeLanguage?: string;
  episodeChannel?: string | null;
  episodeDescription?: string | null;
  episodeCategory?: string | null;
  episodeTags?: any[] | null;
  episodePublishDate?: string | null;
  episodeDurationSeconds?: number | null;
  // 🔥 新增：生成记录和配额控制
  generationRecords?: {
    transcript: boolean;
    summary: boolean;
    vocabulary: boolean;
    mindmap: boolean;
    cleanread: boolean;
  };
  // 🔥 是否允许 Basic Plan Lite 用户访问（简化：替代 isFeatured）
  allowBasicPlanLite?: boolean;
}

const CEFR_LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'] as const;
type CefrLevel = (typeof CEFR_LEVELS)[number];
const CEFR_LEVEL_SET = new Set<string>(CEFR_LEVELS);

export const VocabularySection = React.memo(
  ({
    vocabulary,
    isVocabularyLoading,
    onGenerateVocabulary,
    onTimestampClick,
    episodeId,
    onVocabularyUpdate,
    onCardSwipe,
    hiddenTermKeys,
    onFilterHiddenTermsChange,
    session,
    userSettings,
    episodeTitle,
    episodeThumbnail,
    transcript,
    episodeLanguage = 'Chinese',
    episodeChannel,
    episodeDescription,
    episodeCategory,
    episodeTags,
    episodePublishDate,
    episodeDurationSeconds,
    generationRecords,
    allowBasicPlanLite = false,
  }: VocabularySectionProps) => {
    const contentRef = useRef<HTMLDivElement>(null);
    const { t } = useI18n();
    const { usageInfo } = useUsage();
    const { user } = useUser();
    const accessToken = session?.access_token as string | undefined;

    // 🔥 检查用户是否是 Basic Plan Lite
    const isBasicPlanLite = useMemo(() => {
      const planType = usageInfo?.planType;
      return planType === 'basic_lite' || usageInfo?.isBasicPlanLite === true;
    }, [usageInfo?.planType, usageInfo?.isBasicPlanLite]);

    // 🔥 检查是否是 Free Plan（使用次数配额而不是时间配额）
    const isFreePlan = usageInfo?.planType === 'free';

    const userCefrLevel = useMemo<CefrLevel | null>(() => {
      const rawLevel = typeof userSettings?.cefrLevel === 'string'
        ? userSettings.cefrLevel.toUpperCase()
        : '';
      return CEFR_LEVEL_SET.has(rawLevel) ? (rawLevel as CefrLevel) : null;
    }, [userSettings?.cefrLevel]);

    const allowedLevels = useMemo<CefrLevel[]>(() => {
      if (!userCefrLevel) {
        return [...CEFR_LEVELS];
      }
      const levelIndex = CEFR_LEVELS.indexOf(userCefrLevel);
      if (levelIndex === -1) {
        return [...CEFR_LEVELS];
      }
      const upperBound = Math.min(CEFR_LEVELS.length - 1, levelIndex + 1);
      return CEFR_LEVELS.slice(0, upperBound + 1);
    }, [userCefrLevel]);

    const allowedLevelSet = useMemo(() => new Set<string>(allowedLevels), [allowedLevels]);
    
    // Enhancement state
    const [isEnhancingVocabulary, setIsEnhancingVocabulary] = useState(false);
    const [hasEnhancedContent, setHasEnhancedContent] = useState(false);
    const [revealAllExplanations, setRevealAllExplanations] = useState(false);
    const [activeLevels, setActiveLevels] = useState<Set<string>>(
      () => new Set<string>(CEFR_LEVELS)
    );
    
    // Vocabulary generation state
    const [isGeneratingVocabulary, setIsGeneratingVocabulary] = useState(false);
    
    // 🔥 新增：跟踪用户是否已经生成了vocabulary
    const [hasGeneratedVocabulary, setHasGeneratedVocabulary] = useState(() => {
      if (typeof window !== 'undefined' && episodeId) {
        const key = `vocabulary-generated-${episodeId}`;
        return localStorage.getItem(key) === 'true';
      }
      return false;
    });

    // 🔥 新增：跟踪当前会话中用户是否已通过扣费确认流程
    // 与 TranscriptSection 的 transcriptConfirmedInSession 保持一致
    const [vocabularyConfirmedInSession, setVocabularyConfirmedInSession] = useState(false);
    
    // 🔥 跨组件通信：监听 TranscriptSection 的确认状态
    // 使用 window.__pageLoadId 来区分当前页面加载和刷新后的情况
    const [transcriptConfirmedInSession, setTranscriptConfirmedInSession] = useState(false);
    
    // 监听 sessionStorage 变化（同一页面内跨组件通信）
    useEffect(() => {
      const checkAndUpdate = () => {
        if (typeof window !== 'undefined' && episodeId) {
          // 获取当前页面加载 ID（存储在 window 对象上，刷新后会重置）
          const win = window as typeof window & { __pageLoadId?: string };
          const currentPageLoadId = win.__pageLoadId;
          // 获取 transcript 确认时的页面加载 ID
          const confirmedPageLoadId = sessionStorage.getItem(`session-transcript-confirmed-page-id-${episodeId}`);
          
          // 只有当确认是在当前页面加载期间发生的才有效
          if (currentPageLoadId && confirmedPageLoadId === currentPageLoadId) {
            setTranscriptConfirmedInSession(true);
          }
        }
      };
      
      // 🔥 组件挂载时立即检查（处理 tab 切换时组件延迟渲染的情况）
      checkAndUpdate();
      
      // 监听自定义事件（同 tab）
      window.addEventListener('transcript-confirmed', checkAndUpdate);
      
      return () => {
        window.removeEventListener('transcript-confirmed', checkAndUpdate);
      };
    }, [episodeId]);

    // 🔥 Quota info for EmptyState display
    const minutesToDeduct = useMemo(() => {
      if (
        typeof episodeDurationSeconds === "number" &&
        Number.isFinite(episodeDurationSeconds) &&
        episodeDurationSeconds > 0
      ) {
        return Math.ceil(episodeDurationSeconds / 60);
      }
      return 1;
    }, [episodeDurationSeconds]);

    const transcriptMinutesRemaining = usageInfo?.transcriptMinutes?.remaining ?? null;
    const insufficientTranscriptMinutes =
      typeof transcriptMinutesRemaining === "number" &&
      transcriptMinutesRemaining >= 0 &&
      transcriptMinutesRemaining < minutesToDeduct;

    // 🔥 统一的配额不足检查（适用于 Free Plan 的次数配额和其他计划的时间配额）
    const insufficientQuota = isFreePlan 
      ? (usageInfo?.remainingTranscripts ?? 0) <= 0  // Free Plan: 检查次数
      : insufficientTranscriptMinutes;               // 其他: 检查时间

    const quotaInfo = useMemo(() => {
      // 如果用户已经有生成记录，不显示配额信息
      if (generationRecords?.vocabulary) {
        return null;
      }
      // 如果用户是 premium 且有无限配额，也不显示
      if (usageInfo?.isPremium && (usageInfo?.transcriptMinutes?.limit ?? 0) < 0) {
        return null;
      }
      
      // 🔥 Free Plan 用户使用次数配额
      if (isFreePlan) {
        const remainingCount = usageInfo?.remainingTranscripts ?? 0;
        return {
          cost: 1, // 每次生成消耗 1 次
          remaining: remainingCount,
          costLabel: t('transcript.dialog.cost', 'Cost'),
          remainingLabel: t('transcript.dialog.remaining', 'Remaining'),
          unitLabel: t('common.count.times', 'times'), // 次数单位
          insufficient: remainingCount <= 0,
        };
      }
      
      // 🔥 其他用户（Basic/Premium）使用时间配额
      return {
        cost: minutesToDeduct,
        remaining: transcriptMinutesRemaining,
        costLabel: t('transcript.dialog.cost', 'Cost'),
        remainingLabel: t('transcript.dialog.remaining', 'Remaining'),
        unitLabel: t('common.time.minutes', 'min'),
        insufficient: insufficientTranscriptMinutes,
      };
    }, [generationRecords?.vocabulary, usageInfo?.isPremium, usageInfo?.transcriptMinutes?.limit, usageInfo?.remainingTranscripts, isFreePlan, minutesToDeduct, transcriptMinutesRemaining, insufficientTranscriptMinutes, t]);

    // 🔥 检查是否应该显示内容（与 TranscriptSection 保持一致）
    const shouldShowVocabulary = useCallback(() => {
      const hasContent = !!(vocabulary?.scenarios && vocabulary.scenarios.length > 0);
      
      // 🎯 allowBasicPlanLite=true 视频：对所有付费用户（Basic Plan Lite / Basic / Premium）自动确认
      // 不显示 "Generate Vocabulary" 按钮，直接显示内容
      if (allowBasicPlanLite && hasContent) {
        console.log('🔍 [shouldShowVocabulary] allowBasicPlanLite=true with content, auto-confirming');
        return true;
      }

      // 🔥 如果正在生成中且有数据，立即显示
      if (isGeneratingVocabulary && hasContent) {
        return true;
      }

      // 🔥 优先检查：如果当前会话中已确认生成（通过点击 Generate 按钮），直接显示内容
      if (vocabularyConfirmedInSession && hasContent) {
        console.log('🔍 [shouldShowVocabulary] Vocabulary confirmed in current session, showing content');
        return true;
      }
      
      // 🔥 如果当前会话中 Transcript 已确认（跨组件共享），Vocabulary 也可以直接显示
      if (transcriptConfirmedInSession && hasContent) {
        console.log('🔍 [shouldShowVocabulary] Transcript confirmed in current session, showing vocabulary content');
        return true;
      }

      // 如果没有 generationRecords 对象，说明 API 还没返回或用户未登录
      if (!generationRecords) {
        // 🔥 allowBasicPlanLite=true 时，即使没有 generationRecords，也尝试显示内容
        if (allowBasicPlanLite && hasContent) {
          console.log('🔍 [shouldShowVocabulary] allowBasicPlanLite=true, no generationRecords but has content');
          return true;
        }
        console.log('🔍 [shouldShowVocabulary] No generationRecords, showing Generate button');
        return false;
      }
      
      // 🔥🔥 统一付费行为：如果用户已为 Transcript 付费，Vocabulary 也可以直接显示
      // 这样用户只需要付费一次，就可以访问所有内容
      if (generationRecords.transcript && hasContent) {
        console.log('🔍 [shouldShowVocabulary] User has transcript record, showing vocabulary content');
        return true;
      }

      // 如果用户有 vocabulary 生成记录，显示内容
      if (generationRecords.vocabulary && hasContent) {
        return true;
      }
      
      // 🔥 关键检查：如果 Redis 中没有任何生成记录，
      // 且用户在当前会话中没有点击过 Generate，显示 "Generate Vocabulary" 按钮
      // 但如果 allowBasicPlanLite=true，即使没有生成记录，也自动确认显示内容
      if (!generationRecords.transcript && !generationRecords.vocabulary) {
        // 🎯 allowBasicPlanLite=true 时自动确认，不显示 Generate 按钮
        if (allowBasicPlanLite && hasContent) {
          console.log('🔍 [shouldShowVocabulary] allowBasicPlanLite=true, no generation record but has content, auto-confirming');
          return true;
        }
        console.log('🔍 [shouldShowVocabulary] No generation records in Redis, showing Generate button', {
          hasLocalStorageRecord: hasGeneratedVocabulary,
          hasContent,
          allowBasicPlanLite,
        });
        return false;
      }

      // 根据 Redis 生成记录决定是否显示
      return generationRecords.vocabulary || generationRecords.transcript;
    }, [generationRecords, vocabulary, hasGeneratedVocabulary, isGeneratingVocabulary, vocabularyConfirmedInSession, transcriptConfirmedInSession, allowBasicPlanLite]);

    // Check if vocabulary already has enhanced content
    useEffect(() => {
      if (vocabulary?.scenarios) {
        const hasEnhanced = vocabulary.scenarios.some((scenario: any) => 
          scenario.terms?.some((term: any) => 
            term.context_description || 
            term.examples?.some((example: any) => example.type === 'practical')
          )
        );
        setHasEnhancedContent(hasEnhanced);
      }
    }, [vocabulary]);

    useEffect(() => {
      if (contentRef.current) {
        contentRef.current.scrollTop = 0;
      }
      setRevealAllExplanations(false);
    }, [vocabulary]);

    const levelCounts = useMemo<Record<CefrLevel, number>>(() => {
      const counts = {} as Record<CefrLevel, number>;
      CEFR_LEVELS.forEach((level) => {
        counts[level] = 0;
      });

      if (vocabulary?.scenarios) {
        vocabulary.scenarios.forEach((scenario: any) => {
          scenario?.terms?.forEach((term: any) => {
            const termLevel = typeof term.level === 'string' ? term.level.toUpperCase() : '';
            if (CEFR_LEVEL_SET.has(termLevel)) {
              counts[termLevel as CefrLevel] += 1;
            }
          });
        });
      }

      return counts;
    }, [vocabulary]);

    const availableLevels = useMemo(
      () => CEFR_LEVELS.filter((level) => levelCounts[level] > 0),
      [levelCounts]
    );

    useEffect(() => {
      const allowedWithTerms = allowedLevels.filter((level) => levelCounts[level] > 0);
      const defaultActive = allowedWithTerms.length
        ? allowedWithTerms
        : allowedLevels.length
          ? allowedLevels
          : CEFR_LEVELS;
      setActiveLevels(new Set<string>(defaultActive));
    }, [allowedLevels, levelCounts]);

    const totalTermCount = useMemo(
      () => availableLevels.reduce((sum, level) => sum + (levelCounts[level] || 0), 0),
      [availableLevels, levelCounts]
    );

    const hasCefrVocabulary = availableLevels.length > 0;

    const filteredVocabulary = useMemo(() => {
      const sourceVocabulary = vocabulary;
      if (!sourceVocabulary || !sourceVocabulary.scenarios) return sourceVocabulary;
      if (!availableLevels.length) return sourceVocabulary;
      if (activeLevels.size === availableLevels.length) return sourceVocabulary;

      const filteredScenarios = sourceVocabulary.scenarios
        .map((scenario: any) => {
          const filteredTerms = (scenario.terms || []).filter((term: any) => {
            const level = typeof term.level === 'string' ? term.level.toUpperCase() : '';
            return activeLevels.has(level);
          });

          if (filteredTerms.length === 0) {
            return null;
          }

          return {
            ...scenario,
            terms: filteredTerms,
          };
        })
        .filter((scenario: any): scenario is Record<string, any> => Boolean(scenario));

      return {
        ...sourceVocabulary,
        scenarios: filteredScenarios,
      };
    }, [vocabulary, activeLevels, availableLevels]);

    const filteredOutTermKeys = useMemo(() => {
      if (!vocabulary?.scenarios) {
        return [] as string[];
      }

      if (!availableLevels.length) {
        return [] as string[];
      }

      if (activeLevels.size === availableLevels.length) {
        return [] as string[];
      }

      const keys: string[] = [];

      vocabulary.scenarios.forEach((scenario: any) => {
        const scenarioName = scenario?.name;
        const terms = Array.isArray(scenario?.terms) ? scenario.terms : [];

        terms.forEach((term: any) => {
          const level = typeof term?.level === "string" ? term.level.toUpperCase() : "";
          if (activeLevels.has(level)) {
            return;
          }
          const key = createVocabularyTermKey(term?.term, scenarioName);
          if (key) {
            keys.push(key);
          }
        });
      });

      return keys;
    }, [vocabulary, activeLevels, availableLevels]);

    useEffect(() => {
      if (!onFilterHiddenTermsChange) {
        return;
      }
      onFilterHiddenTermsChange(filteredOutTermKeys);
    }, [filteredOutTermKeys, onFilterHiddenTermsChange]);

    const displayVocabulary = filteredVocabulary;
    const hasFilteredTerms = Boolean(
      displayVocabulary?.scenarios?.some(
        (scenario: any) => Array.isArray(scenario?.terms) && scenario.terms.length > 0
      )
    );
    const showNoResultsMessage = !isVocabularyLoading && !hasFilteredTerms;
    const isAllSelected = activeLevels.size === availableLevels.length;

    const handleSelectAllLevels = useCallback(() => {
      setActiveLevels(new Set<string>(availableLevels));
    }, [availableLevels]);

    const handleToggleLevel = useCallback((level: CefrLevel) => {
      setActiveLevels((previous) => {
        const next = new Set(previous);
        if (next.has(level)) {
          next.delete(level);
        } else {
          next.add(level);
        }
        if (next.size === 0) {
          next.add(level);
        }
        return next;
      });
    }, [allowedLevelSet]);

    // Handle enhance vocabulary
    const handleEnhanceVocabulary = useCallback(async () => {
      if (!episodeId) {
        console.error('Episode ID is required for enhancement');
        return;
      }

      if (hasEnhancedContent && hasCefrVocabulary) {
        console.log('Vocabulary already enhanced');
        return;
      }

      if (!transcript) {
        console.error('Transcript is required for vocabulary enhancement');
        alert('Unable to enhance vocabulary: transcript is missing');
        return;
      }

      setIsEnhancingVocabulary(true);
      try {
        const response = await fetch(`/api/episodes/${episodeId}/vocabulary-gemini`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
          body: JSON.stringify({
            text: transcript,
            language: episodeLanguage,
            title: episodeTitle ?? null,
            channel: episodeChannel ?? null,
            description: episodeDescription ?? null,
            category: episodeCategory ?? null,
            tags: episodeTags ?? null,
            publishDate: episodePublishDate ?? null,
            durationSeconds:
              typeof episodeDurationSeconds === 'number' && Number.isFinite(episodeDurationSeconds)
                ? episodeDurationSeconds
                : null,
          }),
        });

        if (!response.ok || !response.body) {
          const errorText = await response.text();
          throw new Error(errorText || 'Failed to enhance vocabulary');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let vocabularyText = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          vocabularyText += decoder.decode(value, { stream: true });
        }
        vocabularyText += decoder.decode();

        let cleanedText = vocabularyText.trim();
        if (cleanedText.startsWith('```json')) {
          cleanedText = cleanedText.substring(7);
        } else if (cleanedText.startsWith('```')) {
          cleanedText = cleanedText.substring(3);
        }
        if (cleanedText.endsWith('```')) {
          cleanedText = cleanedText.substring(0, cleanedText.length - 3);
        }
        cleanedText = cleanedText.trim();

        const result = JSON.parse(cleanedText);
        if (!result?.vocabulary && result?.scenarios) {
          // API may return the vocabulary object directly
          setHasEnhancedContent(true);
          if (onVocabularyUpdate) {
            onVocabularyUpdate(result);
          }
        } else if (result?.vocabulary) {
          setHasEnhancedContent(true);
          if (onVocabularyUpdate) {
            onVocabularyUpdate(result.vocabulary);
          }
        } else {
          throw new Error('Invalid vocabulary response received');
        }
      } catch (error) {
        console.error('Error enhancing vocabulary:', error);
        alert(`Failed to enhance vocabulary: ${error instanceof Error ? error.message : 'Unknown error'}`);
      } finally {
        setIsEnhancingVocabulary(false);
      }
    }, [
      accessToken,
      episodeCategory,
      episodeChannel,
      episodeDescription,
      episodeDurationSeconds,
      episodeId,
      episodeLanguage,
      episodePublishDate,
      episodeTags,
      episodeTitle,
      hasEnhancedContent,
      hasCefrVocabulary,
      onVocabularyUpdate,
      transcript,
    ]);

    // Handle vocabulary generation
    const handleGenerateVocabulary = useCallback(async () => {
      // 🔥 统一扣除分钟的条件，与 TranscriptSection 保持一致：
      // 只有当用户没有生成记录 **并且** 已经有现存的 vocabulary 数据时才扣除并模拟 loading
      const hasExistingVocabulary = !!(vocabulary?.scenarios && vocabulary.scenarios.length > 0);
      const shouldDeductMinutes = !generationRecords?.vocabulary && hasExistingVocabulary;
      
      console.log('🔍 [VocabularySection] Deduct minutes check:', {
        hasGenerationRecord: generationRecords?.vocabulary,
        hasExistingVocabulary,
        shouldDeductMinutes,
      });
      
      if (shouldDeductMinutes) {
        // 检查配额是否足够（Free Plan 检查次数，其他检查时间）
        if (insufficientQuota) {
          console.error('Insufficient quota for vocabulary generation');
          return;
        }

        // 🔥 开始 loading 状态
        setIsGeneratingVocabulary(true);

        // 调用 /api/transcript/minutes 扣除分钟数
        if (accessToken) {
          try {
            const deductResponse = await fetch('/api/transcript/minutes', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`,
              },
              body: JSON.stringify({
                durationSeconds: episodeDurationSeconds || 60,
                shareId: episodeId,
              }),
            });

            if (!deductResponse.ok) {
              const errorData = await deductResponse.json();
              console.error('Failed to deduct minutes:', errorData);
              setIsGeneratingVocabulary(false);
              return;
            }

            console.log('✅ Minutes deducted successfully for vocabulary generation:', episodeId);
          } catch (deductError) {
            console.error('Failed to deduct minutes:', deductError);
            // 继续尝试，让后端处理
          }
        }

        // 🔥 模拟 3 秒钟 loading（与 TranscriptSection 一致）
        console.log('⏳ [VocabularySection] Starting 3s simulated loading for existing content...');
        await new Promise(resolve => setTimeout(resolve, 3000));
        console.log('✅ [VocabularySection] Simulated loading complete');

        // 🔥 标记当前会话中已确认生成
        setVocabularyConfirmedInSession(true);
        
        // 同时也更新 localStorage
        setHasGeneratedVocabulary(true);
        if (typeof window !== 'undefined' && episodeId) {
          const key = `vocabulary-generated-${episodeId}`;
          localStorage.setItem(key, 'true');
        }
        
        setIsGeneratingVocabulary(false);
        return;
      }

      // 🔥 没有现存内容时，调用实际的生成函数
      setIsGeneratingVocabulary(true);
      // Call onGenerateVocabulary and handle the promise if it returns one
      const result = onGenerateVocabulary();
      if (result && typeof result.then === 'function') {
        result.then(() => {
          // 🔥 标记已经生成了vocabulary
          setVocabularyConfirmedInSession(true);
          setHasGeneratedVocabulary(true);
          if (typeof window !== 'undefined' && episodeId) {
            const key = `vocabulary-generated-${episodeId}`;
            localStorage.setItem(key, 'true');
          }
        }).finally(() => {
          setIsGeneratingVocabulary(false);
        });
      } else {
        // If it's not a promise, reset the state after a short delay
        setTimeout(() => {
          setIsGeneratingVocabulary(false);
        }, 100);
      }
    }, [onGenerateVocabulary, episodeId, generationRecords?.vocabulary, insufficientQuota, accessToken, episodeDurationSeconds, vocabulary]);

    // 🔥 检查是否应该显示升级 dialog
    // Free Plan 用户：有 3 次生成额度，显示 "Generate" 按钮（不显示升级 dialog）
    // Basic Plan Lite 用户：如果 allowBasicPlanLite 为 false，显示升级 dialog
    const shouldShowUpgradeDialog = useMemo(() => {
      // 如果有生成记录（transcript 或 vocabulary），不显示升级 dialog
      if (generationRecords?.transcript || generationRecords?.vocabulary) return false;
      // 如果当前会话已确认，不显示升级 dialog
      if (vocabularyConfirmedInSession || transcriptConfirmedInSession) return false;
      
      // Basic Plan Lite 用户：检查 allowBasicPlanLite 字段
      if (isBasicPlanLite && !allowBasicPlanLite) return true;
      
      return false;
    }, [isBasicPlanLite, allowBasicPlanLite, generationRecords?.transcript, generationRecords?.vocabulary, vocabularyConfirmedInSession, transcriptConfirmedInSession]);

    // 🔥 检查是否应该显示内容
    if (!shouldShowVocabulary() || (!vocabulary && !isVocabularyLoading)) {
      // 🔥 Free Plan 用户或不允许访问的 Basic Plan Lite 用户显示升级 dialog
      if (shouldShowUpgradeDialog) {
        return (
          <div className="flex flex-col w-full bg-background/30 backdrop-blur-sm rounded-3xl border border-border/50">
            {/* Header */}
            <div className="flex items-center justify-between mb-0 sm:mb-6 p-4 border-b border-border/50">
              <div className="flex items-center space-x-3">
                <div className="w-2 h-6 bg-red-500 rounded-full"></div>
                <h3 className="text-xl font-bold text-foreground">{t('vocabulary.section.title')}</h3>
              </div>
            </div>
            {/* 🔥 Free Plan 用户：显示升级提示 */}
            <UpgradePromptDialog
              isOpen={true}
              onClose={() => {
                // 关闭时可以导航回上一页或其他操作
              }}
              errorType="limit_exceeded"
              remainingCount={0}
              limitCount={0}
            />
          </div>
        );
      }

      return (
        <div className="flex flex-col w-full bg-background/30 backdrop-blur-sm rounded-3xl border border-border/50">
          {/* Header */}
          <div className="flex items-center justify-between mb-0 sm:mb-6 p-4 border-b border-border/50">
            <div className="flex items-center space-x-3">
              <div className="w-2 h-6 bg-red-500 rounded-full"></div>
              <h3 className="text-xl font-bold text-foreground">{t('vocabulary.section.title')}</h3>
            </div>
          </div>
          <EmptyState
              title={isGeneratingVocabulary ? t('vocabulary.generating', "Generating Vocabulary Analysis...") : t('vocabulary.generate', "Generate Vocabulary")}
              description={isGeneratingVocabulary ? t('vocabulary.generatingDesc', "Please wait while we analyze the key terms, phrases, and topics from this episode.") : t('vocabulary.generateDesc', "Generate an AI-powered vocabulary analysis to discover key terms, phrases, and topics from this episode.")}
              icons={[<BookOpenIcon key="p1" className="h-6 w-6" />, <Brain key="p2" className="h-6 w-6" />, <Sparkles key="p3" className="h-6 w-6" />]}
              action={{ 
                label: isGeneratingVocabulary ? t('vocabulary.generating', "Generating...") : t('vocabulary.generate', "Generate Vocabulary"), 
                icon: isGeneratingVocabulary ? <SpinnerIcon size="sm" /> : <Brain className="h-4 w-4" />, 
                onClick: handleGenerateVocabulary,
                disabled: isGeneratingVocabulary || insufficientQuota
              }}
              quotaInfo={quotaInfo}
            />
        </div>
      );
    }

    return (
      <>
        {/* Floating Action Buttons - show if vocabulary exists and needs enhancement or lacks CEFR levels */}
        {episodeId && vocabulary && (!hasEnhancedContent || !hasCefrVocabulary) && (
          <VocabularyFloatingActionButtons
            onEnhanceVocabulary={handleEnhanceVocabulary}
            isEnhancingVocabulary={isEnhancingVocabulary}
            hasEnhancedContent={hasEnhancedContent}
          />
        )}

        <div className="flex flex-col w-full bg-background/30 backdrop-blur-sm rounded-3xl border border-border/50">
          {/* Header */}
          <div className="flex items-center justify-between mb-1 sm:mb-4 p-2 sm:p-4 border-b border-border/50">
            <div className="flex items-center space-x-3">
              <div className="w-2 h-6 bg-red-500 rounded-full"></div>
              <h3 className="text-xl font-bold text-foreground">
                {t('vocabulary.section.title')}
              </h3>
            </div>
            {displayVocabulary?.scenarios && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span>
                  {(displayVocabulary.scenarios.length === 1
                    ? t('vocabulary.section.topicCountSingular')
                    : t('vocabulary.section.topicCountPlural')
                  ).replace('{count}', String(displayVocabulary.scenarios.length))}
                </span>
                {hasEnhancedContent && (
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 px-2 text-xs"
                      onClick={() => setRevealAllExplanations((prev) => !prev)}
                      title={
                        revealAllExplanations
                          ? t('vocabulary.section.restoreBlurTitle')
                          : t('vocabulary.section.revealAllTitle')
                      }
                    >
                      {revealAllExplanations ? (
                        <>
                          <EyeOff className="mr-1 h-4 w-4" />
                          {t('vocabulary.section.restoreBlurButton')}
                        </>
                      ) : (
                        <>
                          <Eye className="mr-1 h-4 w-4" />
                          {t('vocabulary.section.revealAllButton')}
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>

          {vocabulary?.scenarios && totalTermCount > 0 && (
            <div className="px-2 sm:px-4 pb-2 border-b border-border/30">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {t('vocabulary.section.levelFilterLabel')}
                  {userCefrLevel && (
                    <span className="ml-2 normal-case text-muted-foreground/80">
                      {t('vocabulary.section.levelFilterUserLevel').replace('{level}', userCefrLevel)}
                    </span>
                  )}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant={isAllSelected ? 'default' : 'outline'}
                  className={cn(
                    'sm:h-8 h-7 px-3 text-xs rounded-full bg-foreground text-background',
                    !isAllSelected && 'bg-muted/60 hover:bg-muted text-muted-foreground'
                  )}
                  onClick={handleSelectAllLevels}
                >
                  {t('vocabulary.section.levelFilterAll')} ({totalTermCount})
                </Button>
                {availableLevels.map((level) => {
                  const levelCount = levelCounts[level];
                  const isActive = activeLevels.has(level);
                  const isRecommended = allowedLevelSet.has(level);
                  return (
                    <Button
                      key={level}
                      type="button"
                      size="sm"
                      variant={isActive ? 'default' : 'outline'}
                      className={cn(
                        'sm:h-8 h-7 px-3 text-xs rounded-full bg-foreground text-background',
                        !isActive && 'bg-muted/60 hover:bg-muted text-muted-foreground',
                        !isRecommended && 'border-dashed'
                      )}
                      onClick={() => handleToggleLevel(level)}
                    >
                      {level} ({levelCount})
                    </Button>
                  );
                })}
              </div>
            </div>
          )}
          
          {/* Content */}
          <div className="flex-1">
            <div ref={contentRef} className="h-full w-full">
              {showNoResultsMessage ? (
                <div className="p-6 sm:p-8 text-sm text-muted-foreground">
                  {t('vocabulary.section.noTermsMatchFilter')}
                </div>
              ) : (
                <VocabularyDisplay
                  data={displayVocabulary}
                  isLoading={isVocabularyLoading}
                  onTimestampClick={onTimestampClick}
                  forceReveal={revealAllExplanations}
                  onCardSwipe={onCardSwipe}
                  hiddenTermKeys={hiddenTermKeys}
                  session={session}
                  userSettings={userSettings}
                  episodeId={episodeId}
                  episodeTitle={episodeTitle}
                  episodeThumbnail={episodeThumbnail}
                  onVocabularyUpdate={onVocabularyUpdate}
                />
              )}
            </div>
          </div>
        </div>
      </>
    );
  }
);

VocabularySection.displayName = "VocabularySection";
